import React, { useMemo } from 'react';
import MerchantGroup from './components/MerchantGroup';
import CartTotal from './components/CartTotal';

/**
 * CurrencyCheckoutSection - Component for displaying cart items and checkout for a specific currency
 * 
 * This component groups and displays cart items by merchant for a specific currency
 * and provides a checkout button specific to that currency.
 */
const CurrencyCheckoutSection = ({
  currency,
  merchantGroups,
  handleDelete,
  handleQuantityChange,
  isCheckingOut,
  updating,
  checkoutError,
  handleCheckout,
  isFirstSection,
  hasMultipleCurrencies
}) => {
  // Debug
  React.useEffect(() => {
    console.log(`CurrencyCheckoutSection for ${currency}:`, {
      merchantGroups
    });
  }, [currency, merchantGroups]);

  // Create checkout handler specific to this currency
  const handleCurrencyCheckout = () => {
    // Pass the currency to the checkout handler
    handleCheckout(currency);
  };

  // Calculate totals for this currency section
  const { totalAmount, depositAmount, hasShippingItems, hasRentalItemsInGroup } = useMemo(() => {
    let sectionTotal = 0;
    let sectionDeposit = 0;
    let sectionHasShipping = false;
    let sectionHasRentals = false;

    // Loop through all merchants for this currency
    Object.values(merchantGroups).forEach(merchantGroup => {
      // Sum up prices for all products in this group
      merchantGroup.products.forEach(product => {
        // Item type is now on product.type
        const itemType = product.type; 

        if (product.stock !== '0') {
          let itemPrice = 0;
          if (itemType === 'rent') {
            sectionHasRentals = true;
            if (product.rentalPeriod?.price) {
              itemPrice = product.rentalPeriod.price;
            } else if (product.rentPriceAndTime?.length > 0 && product.rentalPeriod?.option && product.rentalPeriod?.number) {
              // Fallback: try to find the price from rentPriceAndTime based on stored option and number
              const selectedOption = product.rentPriceAndTime.find(
                opt => opt.option === product.rentalPeriod.option && opt.number === product.rentalPeriod.number
              );
              itemPrice = selectedOption?.price || product.price || 0;
            } else {
              itemPrice = product.price || 0; // Fallback to main price if specific rental price not found
            }
            
            let deposit = 0;
            if (product.rentDeposit && Number(product.rentDeposit) > 0) {
              deposit = Number(product.rentDeposit);
            } else if (product.deposit && Number(product.deposit) > 0) { // Fallback to generic deposit field
              deposit = Number(product.deposit);
            }
            sectionDeposit += deposit * (product.quantity || 1);

          } else { // For 'buy' items (or any other type not 'rent')
            itemPrice = product.price || 0;
          }
          
          // Consider productOptions for price adjustments for both buy and rent
          if (product.productOptions && product.productOptions.length > 0) {
            product.productOptions.forEach(option => {
              if (option.priceModifier) {
                itemPrice += parseFloat(option.priceModifier);
              }
            });
          }

          sectionTotal += itemPrice * (product.quantity || 1);
        }
        
        // Check shipping status
        if (product.deliveryMethod === 'shipping') {
          sectionHasShipping = true;
        }
      });
    });

    return {
      totalAmount: sectionTotal,
      depositAmount: sectionDeposit,
      hasShippingItems: sectionHasShipping,
      hasRentalItemsInGroup: sectionHasRentals
    };
  }, [merchantGroups]);

  return (
    <div className={isFirstSection ? '' : 'mt-12 pt-8 border-t border-gray-200'}>
      {/* Currency header - only show when multiple currencies exist */}
      {hasMultipleCurrencies && currency && (
        <h2 className="text-xl font-bold mb-6 text-gray-800">
          {currency} Items
        </h2>
      )}
      
      {/* Display merchant groups for this currency */}
      {Object.entries(merchantGroups).map(([groupKey, merchantGroup]) => {
        return (
          <MerchantGroup
            key={`${groupKey}-${currency}`}
            merchantId={merchantGroup.merchantId}
            group={merchantGroup}
            handleDelete={handleDelete}
            handleQuantityChange={handleQuantityChange}
            currency={currency}
          />
        );
      })}
      
      {/* Display total for this currency */}
      <CartTotal 
        totalAmount={totalAmount}
        depositAmount={depositAmount}
        hasShippingItems={hasShippingItems}
        hasRentalItemsInGroup={hasRentalItemsInGroup}
        isCheckingOut={isCheckingOut}
        updating={updating}
        checkoutError={checkoutError}
        handleCheckout={handleCurrencyCheckout}
        primaryCurrency={currency}
      />
    </div>
  );
};

export default CurrencyCheckoutSection; 